<template>
    <div id="product-report-widget-component" class="pa-4">
        <div class="d-flex flex-wrap align-start justify-space-between gap-2 mb-4">
            <div class="d-flex align-center gap-2">
                <v-icon color="success" size="36">mdi-leaf</v-icon>
                <div>
                    <p class="font-18 font-weight-bold text-uppercase mb-0">{{ $t('product_report.title') }}</p>
                    <p class="font-12 font-color-medium mb-0">{{ $t('product_report.subtitle') }}</p>
                </div>
            </div>

            <v-menu v-model="is_menu_open" :close-on-content-click="false" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <div
                        :class="contracts.length > 0 ? 'pointer' : ''"
                        class="contract-selector pa-2"
                        data-cy="contract-selector"
                        v-bind="attrs"
                        v-on="contracts.length > 0 ? on : {}"
                    >
                        <p class="font-10 text-uppercase font-color-medium mb-0">{{ $t('product_report.select_contract') }}</p>
                        <div class="d-flex align-center justify-space-between gap-2">
                            <span class="font-12 text-overflow-ellipsis">{{ activator_label }}</span>
                            <v-icon v-if="contracts.length > 0">mdi-chevron-down</v-icon>
                        </div>
                    </div>
                </template>

                <v-list class="py-0">
                    <v-list-item
                        v-for="contract in contracts"
                        :key="contract.id_customer_contrat"
                        :input-value="contract.id_customer_contrat === selected_contract_id"
                        @click="onContractSelected(contract)"
                    >
                        <div class="py-2">
                            <div class="d-flex flex-wrap align-center gap-2">
                                <span class="font-12">{{ contractPeriodLabel(contract) }}</span>
                                <v-chip v-if="isContractActive(contract)" color="success" x-small>
                                    {{ $t('product_report.active_contract') }}
                                </v-chip>
                            </div>
                            <span v-if="contractVolume(contract) !== null" class="font-10 font-color-medium">
                                {{ contractVolume(contract) }} L
                            </span>
                            <p
                                v-if="contract.id_customer_contrat === selected_contract_id && analyzed_period_label"
                                class="font-10 font-color-medium mb-0"
                            >
                                {{ $t('product_report.analyzed_period') }} : {{ analyzed_period_label }}
                            </p>
                        </div>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <template v-if="!fetching_dashboard">
            <v-row>
                <v-col cols="12" lg="6">
                    <CanistersSplitWidgetComponent :contract="dashboard.contract" :totals="totals" />
                </v-col>

                <v-col cols="12" lg="6">
                    <ProductDetailWidgetComponent :products="products" :totals="totals" />
                </v-col>

                <v-col cols="12" lg="6">
                    <WaterAndSolutionWidgetComponent :totals="totals" />
                </v-col>

                <v-col cols="12" lg="6">
                    <Co2TransportWidgetComponent :client_code="dashboard.client_code" :totals="totals" :transport="transport" />
                </v-col>

                <v-col cols="12">
                    <ResponsibleChoiceWidgetComponent :totals="totals" :transport="transport" />
                </v-col>
            </v-row>

            <div class="d-flex flex-wrap justify-space-between gap-2 font-10 font-color-medium mt-3">
                <span>{{ $t('product_report.data_note') }}</span>
                <span>{{ $t('product_report.edition_date', { date: edition_date }) }}</span>
            </div>
        </template>

        <v-skeleton-loader v-else type="image, table" />
    </div>
</template>

<script>
import moment from 'moment'
import CanistersSplitWidgetComponent from '@/components/site/widget/CanistersSplitWidgetComponent.vue'
import Co2TransportWidgetComponent from '@/components/site/widget/Co2TransportWidgetComponent.vue'
import ProductDetailWidgetComponent from '@/components/site/widget/ProductDetailWidgetComponent.vue'
import ResponsibleChoiceWidgetComponent from '@/components/site/widget/ResponsibleChoiceWidgetComponent.vue'
import WaterAndSolutionWidgetComponent from '@/components/site/widget/WaterAndSolutionWidgetComponent.vue'
import ClientRepository from '@/repositories/ClientRepository'
import DomicileRepository from '@/repositories/DomicileRepository'
import { EmptySuicleanDashboard } from '@/models/ISuicleanDashboard'

export default {
    name: 'ProductReportWidgetComponent',
    components: {
        CanistersSplitWidgetComponent,
        Co2TransportWidgetComponent,
        ProductDetailWidgetComponent,
        ResponsibleChoiceWidgetComponent,
        WaterAndSolutionWidgetComponent,
    },
    props: {
        id_domicile: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            fetching_dashboard: true,
            dashboard: { ...EmptySuicleanDashboard },
            contracts: [],
            selected_contract_id: null,
            is_menu_open: false,
        }
    },
    computed: {
        totals() {
            return this.dashboard.totals
        },

        transport() {
            return this.dashboard.transport
        },

        products() {
            return this.dashboard.products
        },

        edition_date() {
            return moment().format(this.date_format)
        },

        selected_contract() {
            return this.contracts.find((contract) => contract.id_customer_contrat === this.selected_contract_id)
        },

        activator_label() {
            const contract = this.selected_contract
            let label = this.$t('product_report.no_active_contract')

            if (contract) {
                const parts = [this.contractPeriodLabel(contract)]
                if (this.contractVolume(contract) !== null) {
                    parts.push(`${this.contractVolume(contract)} L`)
                }
                if (this.isContractActive(contract)) {
                    parts.push(this.$t('product_report.active_contract'))
                }
                label = parts.join(' · ')
            }

            return label
        },

        analyzed_period_label() {
            const range = this.dashboard.range

            return range.date_begin && range.date_end
                ? `${moment(range.date_begin).format(this.date_format)} → ${moment(range.date_end).format(this.date_format)}`
                : ''
        },
    },
    methods: {
        isContractActive(contract) {
            const begin = moment(contract.date_begin)
            const end = contract.date_end ? moment(contract.date_end) : begin.clone().add(contract.duration_months, 'month')

            return !begin.isAfter(moment()) && end.isAfter(moment())
        },

        contractVolume(contract) {
            const volume = contract.metadata ? contract.metadata.contract_volume : null

            return volume !== null && volume !== undefined ? Number(volume) : null
        },

        contractPeriodLabel(contract) {
            return this.$t('product_report.contract_period', {
                start: moment(contract.date_begin).format(this.date_format),
                end: contract.date_end ? moment(contract.date_end).format(this.date_format) : '…',
            })
        },

        onContractSelected(contract) {
            this.is_menu_open = false
            this.selected_contract_id = contract.id_customer_contrat
            this.fetchDashboard()
        },

        fetchContracts() {
            ClientRepository.getContractsOfConnectedClient()
                .then((success) => {
                    this.contracts = success.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
        },

        fetchDashboard() {
            this.fetching_dashboard = true

            const query = new URLSearchParams()
            if (this.selected_contract_id) {
                query.set('id_contract', String(this.selected_contract_id))
            }

            DomicileRepository.getSuicleanDashboard(this.id_domicile, query.toString())
                .then((success) => {
                    this.dashboard = success.data.data
                    this.selected_contract_id = this.dashboard.contract.id_customer_contrat
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetching_dashboard = false
                })
        },
    },
    created() {
        this.fetchContracts()
        this.fetchDashboard()
    },
}
</script>

<style>
#product-report-widget-component .legend-dot {
    height: 12px;
    width: 12px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
}

#product-report-widget-component .contract-selector {
    border: 1px solid var(--v-black-lighten5);
    border-radius: 8px;
    min-width: 240px;
    max-width: 340px;
}

#product-report-widget-component .report-note {
    background-color: rgba(76, 175, 80, 0.1);
    border-radius: 8px;
}

#product-report-widget-component .report-reference {
    border: 1px solid var(--v-success-base);
    border-radius: 8px;
}

#product-report-widget-component .widget-full-height,
#product-report-widget-component .widget-full-height .v-card {
    height: 100%;
}
</style>
